import React from 'react';

import {configureMapColumnNamesModalPane} from 'bulk-import/panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from 'bulk-import/panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_COLUMN_TYPES,
  IMPORT_TYPE,
  FINAL_QUANTITY_COLUMN_TYPES,
  FINAL_CONCENTRATION_COLUMN_TYPES,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import {getDependentColumnTypesError} from 'bulk-import/SpreadsheetBulkImportUtil';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {
  getSampleControlRolesColumns,
  getValidContentsContainerColumns,
} from 'bulk-import/storables/StorableColumnsUtil';
import {Source} from 'constants/SourceConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import * as ModalContainer from 'ui-library/ModalContainer';

interface Props {
  initialRegistry?: Source | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

export default class BulkUpdateContainersFlow extends React.Component<Props> {
  static open(props: Omit<Props, 'onRequestClose'>): void {
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkUpdateContainersFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  private getPanes(): Array<Pane> {
    return [
      {
        paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
        configuration: StorableImportSettingsModalPane.configure({
          schemaSelectorClass: ContainerSchemaSelector,
          schemaIdAccessorKey: 'containerSchemaId',
          showProjectSelector: false,
        }),
      },
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure(),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes() {
            return [
              ...STORABLE_COLUMN_TYPES,
              COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
              ...FINAL_QUANTITY_COLUMN_TYPES,
              ...FINAL_CONCENTRATION_COLUMN_TYPES,
              COLUMN_TYPE.NEW_BARCODE,
              ...getSampleControlRolesColumns(),
              ...getValidContentsContainerColumns(),
              COLUMN_TYPE.IGNORE,
            ];
          },
          getRequiredColumnsError(
            columns: Array<SpreadsheetColumn>,
            options?: {selectedSchemaIds: SelectedSchemaIds}
          ) {
            const hasContainerBarcode = columns.some(({type}) => type === COLUMN_TYPE.BARCODE);
            const hasParentBarcode = columns.some(({type}) => type === COLUMN_TYPE.PARENT_STORABLE_BARCODE);

            const missingOrDuplicateContainerBarcodeError = SpreadsheetBulkImportUtil.getRequiredFieldError(
              [COLUMN_TYPE.BARCODE],
              columns,
              options!.selectedSchemaIds,
              IMPORT_TYPE.UPDATE_CONTAINERS
            );

            const missingOrDuplicateParentBarcodeError = SpreadsheetBulkImportUtil.getRequiredFieldError(
              [COLUMN_TYPE.PARENT_STORABLE_BARCODE],
              columns,
              options!.selectedSchemaIds,
              IMPORT_TYPE.UPDATE_CONTAINERS
            );

            // If one of the barcode column types is included and has an error associated with it (duplicate
            // column type), return that error.
            if (hasContainerBarcode || hasParentBarcode) {
              if (hasContainerBarcode && missingOrDuplicateContainerBarcodeError) {
                return missingOrDuplicateContainerBarcodeError;
              }
              if (hasParentBarcode && missingOrDuplicateParentBarcodeError) {
                return missingOrDuplicateParentBarcodeError;
              }
            }
            // If neither parent nor container barcode types are included also return an error.
            else {
              return missingOrDuplicateContainerBarcodeError;
            }

            const missingOneOfBarcodeOrCoordinatesError = getDependentColumnTypesError(columns, [
              [COLUMN_TYPE.PARENT_STORABLE_BARCODE, COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES],
            ]);

            // If parent barcode was not specified and the there's a dependent group error then we know
            // parent coordinates were specified without a parent barcode, which is invalid
            if (!hasParentBarcode && missingOneOfBarcodeOrCoordinatesError) {
              return missingOneOfBarcodeOrCoordinatesError;
            }

            const measurementColumnsError = getDependentColumnTypesError(columns, [
              FINAL_QUANTITY_COLUMN_TYPES,
              FINAL_CONCENTRATION_COLUMN_TYPES,
            ]);
            if (measurementColumnsError) {
              return measurementColumnsError;
            }

            // Specifying both a barcode for the container itself and a parent barcode means the container is
            // moving locations. This means that the update already won't be a noop, so we don't need to
            // check for columns being updated
            const isMoving = hasContainerBarcode && hasParentBarcode;
            if (
              !isMoving &&
              columns.every(({type}) =>
                [
                  COLUMN_TYPE.IGNORE,
                  COLUMN_TYPE.BARCODE,
                  COLUMN_TYPE.PARENT_STORABLE_BARCODE,
                  COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES,
                ].includes(type)
              )
            ) {
              return 'At least one column to update must be specified.';
            }
            return null;
          },
          onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForUpdateContainers,
        }),
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          enableReplaceLinkWithText: true,
          enableSkipErrors: true,
        }),
      },
    ];
  }

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="container"
        importType={IMPORT_TYPE.UPDATE_CONTAINERS}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.updateContainersFromSpreadsheetWithDataMods}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this.getPanes()}
        title="Update containers"
      />
    );
  }
}
